import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProcessStepService, ProcessStep, ProcessStepRisk, ProcessStepControl } from '../services/ProcessStepService';
import { AdminConfigurationService } from '../services/AdminConfigurationService';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ErrorMessage } from '../components/ui/ErrorMessage';
import { ProcessStepItem } from '../components/RCSA/ProcessStepItem';
import { ProcessStepMetadataModal } from '../components/RCSA/ProcessStepMetadataModal';
import { RiskControlMappingPanel } from '../components/RCSA/RiskControlMappingPanel';
import { RiskAppetiteMetrics } from '../components/RCSA/RiskAppetiteMetrics';
import { RiskScopingWizard } from '../components/RiskScoping';
import { Search, Filter, Plus, FileText, Download, Settings } from 'lucide-react';
import SmartRouterBanner from '../components/SmartRouter/SmartRouterBanner';
import { useSmartRouterStepSync } from '../hooks/useSmartRouterStepSync';

interface ExtendedProcessStep extends ProcessStep {
  risks?: ProcessStepRisk[];
  controls?: ProcessStepControl[];
  inherent_risk_level?: 'low' | 'medium' | 'high';
  residual_risk_level?: 'low' | 'medium' | 'high';
  control_effectiveness_percentage?: number;
}

interface ConfigurationData {
  countries: Array<{ id: string; name: string }>;
  legalEntities: Array<{ id: string; name: string }>;
  supportingModels: Array<{ id: string; name: string }>;
  thirdPartyBusinessTypes: Array<{ id: string; name: string }>;
}

const RCSAProcessSteps: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const smartRouter = useSmartRouterStepSync({
    autoSyncSignal: projectId,
    autoSyncArtifact: projectId
      ? { kind: 'rcsa_process_steps', project_id: projectId }
      : undefined,
    autoSyncSummary: projectId
      ? `Reviewed RCSA process steps for project #${projectId}.`
      : undefined,
  });
  
  // State management
  const [processSteps, setProcessSteps] = useState<ExtendedProcessStep[]>([]);
  const [configurations, setConfigurations] = useState<ConfigurationData>({
    countries: [],
    legalEntities: [],
    supportingModels: [],
    thirdPartyBusinessTypes: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRiskLevel, setFilterRiskLevel] = useState<string>('all');
  const [filterFramework, setFilterFramework] = useState<string>('all');
  const [selectedStepId, setSelectedStepId] = useState<number | null>(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [showRiskScopingModal, setShowRiskScopingModal] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());

  // Load initial data
  useEffect(() => {
    if (projectId) {
      loadProcessSteps();
      loadConfigurations();
    }
  }, [projectId]);

  const loadProcessSteps = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the existing API endpoint with includes for risks and controls
      const response = await fetch(`/api/process-steps/project/${projectId}?include_risks=true&include_controls=true`);
      const data = await response.json();
      
      if (data.success) {
        // Transform the data to match our interface
        const stepsWithMetrics = data.data.map((step: any) => ({
          ...step,
          inherent_risk_level: calculateRiskLevel(step.risks?.reduce((max: any, risk: any) => 
            risk.inherent_risk_score > (max?.inherent_risk_score || 0) ? risk : max, null)?.inherent_risk_score),
          residual_risk_level: calculateRiskLevel(step.risks?.reduce((max: any, risk: any) => 
            risk.residual_risk_score > (max?.residual_risk_score || 0) ? risk : max, null)?.residual_risk_score),
          control_effectiveness_percentage: calculateControlEffectiveness(step.controls)
        }));
        
        setProcessSteps(stepsWithMetrics);
      } else {
        throw new Error(data.message || 'Failed to load process steps');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load process steps');
    } finally {
      setLoading(false);
    }
  };

  const loadConfigurations = async () => {
    try {
      // Load dropdown configurations from admin settings
      const [countries, legalEntities, supportingModels, thirdPartyTypes] = await Promise.all([
        AdminConfigurationService.getByType('country'),
        AdminConfigurationService.getByType('legal_entity'),
        AdminConfigurationService.getByType('supporting_model'),
        AdminConfigurationService.getByType('third_party_business_type')
      ]);

      setConfigurations({
        countries: countries.map(c => ({ id: c.id.toString(), name: c.config_value })),
        legalEntities: legalEntities.map(le => ({ id: le.id.toString(), name: le.config_value })),
        supportingModels: supportingModels.map(sm => ({ id: sm.id.toString(), name: sm.config_value })),
        thirdPartyBusinessTypes: thirdPartyTypes.map(tp => ({ id: tp.id.toString(), name: tp.config_value }))
      });
    } catch (err) {
      console.error('Failed to load configurations:', err);
      // Continue with empty configurations rather than failing completely
    }
  };

  const calculateRiskLevel = (score: number | undefined): 'low' | 'medium' | 'high' => {
    if (!score) return 'low';
    if (score >= 15) return 'high';
    if (score >= 9) return 'medium';
    return 'low';
  };

  const calculateControlEffectiveness = (controls: ProcessStepControl[] | undefined): number => {
    if (!controls || controls.length === 0) return 0;
    const effectiveControls = controls.filter(c => c.control_effectiveness === 'Effective').length;
    return Math.round((effectiveControls / controls.length) * 100);
  };

  // Filter process steps based on search and filters
  const filteredSteps = processSteps.filter(step => {
    const matchesSearch = searchTerm === '' || 
      step.process_step_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      step.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesRiskFilter = filterRiskLevel === 'all' || 
      step.inherent_risk_level === filterRiskLevel;
      
    // Framework filter would need to be implemented based on risk taxonomy
    const matchesFrameworkFilter = filterFramework === 'all'; // Simplified for now
    
    return matchesSearch && matchesRiskFilter && matchesFrameworkFilter;
  });

  const toggleStepExpansion = useCallback((stepId: number) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepId)) {
        newSet.delete(stepId);
      } else {
        newSet.add(stepId);
      }
      return newSet;
    });
  }, []);

  const handleStepUpdate = useCallback(async (stepId: number, updateData: Partial<ProcessStep>) => {
    try {
      const updatedStep = await ProcessStepService.update(stepId, updateData);
      setProcessSteps(prev => prev.map(step => 
        step.id === stepId ? { ...step, ...updatedStep } : step
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update process step');
    }
  }, []);

  const handleAddRisk = useCallback(async (stepId: number, riskData: Partial<ProcessStepRisk>) => {
    try {
      const newRisk = await ProcessStepService.addRisk(stepId, riskData);
      setProcessSteps(prev => prev.map(step => 
        step.id === stepId 
          ? { ...step, risks: [...(step.risks || []), newRisk] }
          : step
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add risk');
    }
  }, []);

  const handleAddControl = useCallback(async (stepId: number, controlData: Partial<ProcessStepControl>) => {
    try {
      const newControl = await ProcessStepService.addControl(stepId, controlData);
      setProcessSteps(prev => prev.map(step => 
        step.id === stepId 
          ? { ...step, controls: [...(step.controls || []), newControl] }
          : step
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add control');
    }
  }, []);

  const exportToExcel = async () => {
    try {
      // This would call the export service
      const response = await fetch(`/api/export/rcsa/${projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format: 'xlsx', includeAll: true })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `RCSA-Export-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError('Failed to export RCSA data');
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} onRetry={loadProcessSteps} />;

  return (
    <div className="min-h-screen bg-gray-50">
      {smartRouter.inRouter && (
        <SmartRouterBanner
          stepLabel="RCSA Process Steps (Smart Router)"
          canFinish={!!projectId}
          disabledReason="Process steps must be loaded for the routed project before finishing."
          buildCompletionPayload={async () => {
            await smartRouter.finishStep(
              { kind: 'rcsa_process_steps', project_id: projectId },
              `Reviewed RCSA process steps for project #${projectId}.`,
            );
            return null;
          }}
        />
      )}
      {/* Header */}
      <div className="bg-[#221551] text-white">
        <div className="px-8 py-6">
          <h1 className="text-2xl font-semibold">Risk & Control Self Assessment (RCSA)</h1>
          <div className="text-sm mt-1 text-white/80">
            Risk Management &gt; RCSA &gt; Process Steps
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-6">
        {/* Process Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border-l-4 border-[#57C8F4]">
          <h2 className="text-xl font-semibold text-[#221551] mb-4">
            Process Steps - Risk & Control Mapping
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div>
              <div className="text-xs font-medium text-[#454F83] uppercase tracking-wide mb-1">
                Total Steps
              </div>
              <div className="text-lg font-semibold text-[#221551]">
                {processSteps.length}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-[#454F83] uppercase tracking-wide mb-1">
                High Risk
              </div>
              <div className="text-lg font-semibold text-[#221551]">
                {processSteps.filter(s => s.inherent_risk_level === 'high').length}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-[#454F83] uppercase tracking-wide mb-1">
                Total Controls
              </div>
              <div className="text-lg font-semibold text-[#221551]">
                {processSteps.reduce((sum, s) => sum + (s.controls?.length || 0), 0)}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-[#454F83] uppercase tracking-wide mb-1">
                Avg. Control Effectiveness
              </div>
              <div className="text-lg font-semibold text-[#221551]">
                {Math.round(processSteps.reduce((sum, s) => sum + (s.control_effectiveness_percentage || 0), 0) / processSteps.length)}%
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-[#454F83] uppercase tracking-wide mb-1">
                Last Assessment
              </div>
              <div className="text-sm font-medium text-[#221551]">
                {new Date().toLocaleDateString()}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-[#454F83] uppercase tracking-wide mb-1">
                Next Review Due
              </div>
              <div className="text-sm font-medium text-[#221551]">
                {new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        {/* Risk Appetite Metrics */}
        <RiskAppetiteMetrics projectId={projectId!} />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Controls Header */}
            <div className="bg-white rounded-lg shadow-sm mb-6">
              <div className="bg-[#C1E5F5] px-6 py-4 rounded-t-lg border-b-2 border-[#30A8CF]">
                <h3 className="text-lg font-semibold text-[#221551]">
                  Process Steps & Risk Controls
                </h3>
                <div className="flex items-center gap-4 mt-4">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Search steps, risks, or controls..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 w-full border border-[#30A8CF] rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    />
                  </div>
                  <button
                    onClick={() => setShowRiskScopingModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#9D7AB8] text-white rounded-md hover:bg-[#454F83] transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Risk Scoping
                  </button>
                  <button
                    onClick={() => setFilterRiskLevel(filterRiskLevel === 'all' ? 'high' : 'all')}
                    className="flex items-center gap-2 px-4 py-2 bg-[#454F83] text-white rounded-md hover:bg-[#9D7AB8] transition-colors"
                  >
                    <Filter className="h-4 w-4" />
                    Risk Level
                  </button>
                  <button
                    onClick={() => setShowMetadataModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#57C8F4] text-white rounded-md hover:bg-[#30A8CF] transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add Step
                  </button>
                  <button
                    onClick={exportToExcel}
                    className="flex items-center gap-2 px-4 py-2 bg-[#30A8CF] text-white rounded-md hover:bg-[#57C8F4] transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Export
                  </button>
                </div>
              </div>

              {/* Process Steps List */}
              <div className="divide-y divide-gray-200">
                {filteredSteps.map((step) => (
                  <ProcessStepItem
                    key={step.id}
                    step={step}
                    isExpanded={expandedSteps.has(step.id)}
                    onToggleExpansion={toggleStepExpansion}
                    onUpdateStep={handleStepUpdate}
                    onAddRisk={handleAddRisk}
                    onAddControl={handleAddControl}
                    configurations={configurations}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Risk Taxonomy Overview */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="bg-[#454F83] text-white px-6 py-4 rounded-t-lg">
                <h4 className="font-semibold">Risk Taxonomy Overview</h4>
              </div>
              <div className="p-6">
                <div className="space-y-4 text-sm">
                  <div>
                    <div className="font-medium text-[#221551]">Operational Risk</div>
                    <div className="text-[#454F83]">Level 1 Risk Category - Process execution and systems</div>
                  </div>
                  <div>
                    <div className="font-medium text-[#221551]">Financial Crime Risk</div>
                    <div className="text-[#454F83]">Level 2: Compliance & Legal Risk</div>
                  </div>
                  <div>
                    <div className="font-medium text-[#221551]">Credit Risk</div>
                    <div className="text-[#454F83]">Level 1 Risk Category - Counterparty default</div>
                  </div>
                  <div>
                    <div className="font-medium text-[#221551]">Legal Risk</div>
                    <div className="text-[#454F83]">Level 2: Compliance & Legal Risk</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Assessment History */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="bg-[#454F83] text-white px-6 py-4 rounded-t-lg">
                <h4 className="font-semibold">Assessment History</h4>
              </div>
              <div className="p-6">
                <div className="space-y-4 text-sm">
                  <div>
                    <div className="font-medium text-[#221551]">
                      {new Date().toLocaleDateString()}
                    </div>
                    <div className="text-gray-600">Current assessment in progress</div>
                  </div>
                  <div>
                    <div className="font-medium text-[#221551]">Mar 15, 2025</div>
                    <div className="text-gray-600">Quarterly assessment completed</div>
                  </div>
                  <div>
                    <div className="font-medium text-[#221551]">Dec 15, 2024</div>
                    <div className="text-gray-600">Annual risk assessment</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showMetadataModal && (
        <ProcessStepMetadataModal
          isOpen={showMetadataModal}
          onClose={() => setShowMetadataModal(false)}
          onSave={async (stepData) => {
            try {
              const newStep = await ProcessStepService.create({
                ...stepData,
                project_id: parseInt(projectId!),
                step_number: processSteps.length + 1
              });
              setProcessSteps(prev => [...prev, newStep]);
              setShowMetadataModal(false);
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Failed to create process step');
            }
          }}
          configurations={configurations}
        />
      )}

      {/* Risk Scoping Wizard Modal */}
      {showRiskScopingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-auto m-4">
            <div className="sticky top-0 bg-[#221551] text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold">Risk Scoping Configuration</h2>
              <button
                onClick={() => setShowRiskScopingModal(false)}
                className="text-white hover:text-gray-300 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <RiskScopingWizard
                tenantId={1}
                onComplete={() => {
                  setShowRiskScopingModal(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RCSAProcessSteps;